/**
 * onboardingHydrate.js
 *
 * Shared helper that hydrates sessionStorage from a saved onboarding
 * tracker record. Used by surfaces that need to resume an abandoned
 * onboarding from where the user left off:
 *
 *   - Dashboard (multi-agent picker — shows the Draft Agent card
 *     when the user has 0 agents)
 *   - FastAgentDetails (single-agent home — same Draft card lives
 *     here for 0-agent users so we don't bounce them to /fork)
 *
 * Why a shared helper:
 *   The onboarding flow's downstream step components (BusinessStep,
 *   AboutBusiness, Step1..7, etc.) all read their initial values
 *   from sessionStorage. If we navigate the user to those screens
 *   WITHOUT pre-populating sessionStorage from their saved tracker
 *   data, they see a blank form and start over — exactly the
 *   regression we were fixing.
 *
 *   The hydration logic used to live inline in Dashboard.js. Pulling
 *   it out here keeps Dashboard and FastAgentDetails in sync so a
 *   fix to one applies to both automatically.
 */

import { getOnBoardingTracker } from "../Store/apiStore";

/**
 * Walk the tracker's `details` array and write everything we know
 * about the user's previous onboarding answers into sessionStorage.
 *
 * Mirrors Dashboard.hydrateSessionFromTracker (kept for reference if
 * you ever need to debug a missing key — both functions live in lock
 * step). Any new key added here MUST also be removed by the discard
 * path so a "start fresh" tap genuinely clears state.
 *
 * @param {Array<{stepNo:number,data:object}>} stepsWithNo - tracker steps sorted by stepNo
 * @param {Array<{stepName:string,data:object}>} details - full tracker details array
 * @param {{email?:string,userId?:string,businessId?:string|number}} tracker - tracker root
 */
export function hydrateSessionFromTracker(stepsWithNo, details, tracker) {
  const getStepData = (stepNo) =>
    stepsWithNo.find((s) => s.stepNo === stepNo)?.data || {};

  const step0 = getStepData(0);
  const step1 = getStepData(1);
  const step3 = getStepData(3);
  const step4 = getStepData(4);
  const step5 = getStepData(5);
  const step6 = getStepData(6);
  const step7 = getStepData(7);

  // Generic search across all details for a key — falls back when a
  // specific step's data slot doesn't have what we want.
  const findInDetails = (key) => {
    for (const d of details) {
      if (d?.data && d.data[key] !== undefined) return d.data[key];
    }
    return undefined;
  };

  // ---------- BUSINESS / LOCATION ----------
  const businessType = step0.businessType ?? findInDetails("businessType");
  const subType = step0.subType ?? findInDetails("subType");
  if (businessType) sessionStorage.setItem("businessType", businessType);
  if (subType) sessionStorage.setItem("subType", subType);

  if (Array.isArray(step1.buisnessService)) {
    sessionStorage.setItem(
      "selectedServices",
      JSON.stringify(step1.buisnessService),
    );
  }

  const placeDetails =
    step3.businessDetails ||
    step4.businessData ||
    findInDetails("placeDetailsExtract");
  if (placeDetails) {
    sessionStorage.setItem("placeDetailsExtract", JSON.stringify(placeDetails));
  }

  const googleListing = step3.googleListing || findInDetails("googleListing");
  if (googleListing) sessionStorage.setItem("googleListing", googleListing);

  const businessUrl =
    step3.businessUrl ||
    step4.businessData?.website ||
    findInDetails("businessUrl");
  if (businessUrl) sessionStorage.setItem("businessUrl", businessUrl);

  if (Object.keys(step3).length) {
    sessionStorage.setItem("aboutBusinessForm", JSON.stringify(step3));
  }

  // bId / businessId — normalised so downstream components reading
  // either key get a consistent value.
  let rawBId =
    findInDetails("bId") ??
    findInDetails("businessId") ??
    step1.businessId ??
    step4.businessData?.businessId ??
    tracker?.businessId;
  if (rawBId !== undefined && rawBId !== null) {
    const normalizedBId = isNaN(Number(rawBId)) ? rawBId : Number(rawBId);
    sessionStorage.setItem("bId", String(normalizedBId));
    sessionStorage.setItem(
      "businessId",
      JSON.stringify({ businessId: normalizedBId }),
    );
  }

  if (placeDetails) {
    const name = placeDetails.businessName || placeDetails.name || "";
    const city = placeDetails.city;
    const state = placeDetails.state;
    const country = placeDetails.country;
    const address = placeDetails.address;
    let displayBusinessName = "";
    if (name && city && state && country) {
      displayBusinessName = `${name}, ${city}, ${state}, ${country}`;
    } else if (name && address) {
      displayBusinessName = `${name}, ${address}`;
    } else {
      displayBusinessName =
        findInDetails("displayBusinessName") || name || address || "";
    }
    if (displayBusinessName) {
      sessionStorage.setItem("displayBusinessName", displayBusinessName);
    }
  }

  const knowledgeBaseId =
    step4.knowledgeBaseId || findInDetails("knowledgeBaseId");
  if (knowledgeBaseId) sessionStorage.setItem("knowledgeBaseId", knowledgeBaseId);

  // ---------- LANGUAGE / VOICE / AGENT INFO ----------
  if (step5.selectedLang) {
    sessionStorage.setItem("selectedLang", step5.selectedLang);
    sessionStorage.setItem("agentLanguage", step5.selectedLang);
  }
  if (step5.selectedLangCode) {
    sessionStorage.setItem("selectedLangCode", step5.selectedLangCode);
    sessionStorage.setItem("agentLanguageCode", step5.selectedLangCode);
  }

  if (step6.gender) sessionStorage.setItem("agentGender", step6.gender);
  if (step6.voice) {
    sessionStorage.setItem("agentVoice", step6.voice.voice_id || "");
    sessionStorage.setItem("agentVoiceAccent", step6.voice.accent || "");
    sessionStorage.setItem("VoiceAgentName", step6.voice.voice_name || "");
  }

  if (step7.agentName || step7.name || step7.data?.name) {
    const agentName = step7.agentName || step7.name || step7.data?.name;
    sessionStorage.setItem("agentName", agentName);
  }
  if (step7.avatar) {
    const avatarPath = step7.avatar.img || step7.avatar;
    sessionStorage.setItem("avatar", avatarPath);
    sessionStorage.setItem("avtarChecked", "true");
  }

  const AgentCode = findInDetails("AgentCode") || findInDetails("agentCode");
  if (AgentCode) sessionStorage.setItem("AgentCode", AgentCode);

  sessionStorage.setItem(
    "agentRole",
    findInDetails("agentRole") || "General Receptionist",
  );

  const chatWebWidget = findInDetails("chatWebWidget");
  sessionStorage.setItem(
    "chatWebWidget",
    chatWebWidget !== undefined ? JSON.stringify(chatWebWidget) : "true",
  );

  const agentNote = findInDetails("agentNote");
  if (agentNote !== undefined) sessionStorage.setItem("agentNote", agentNote);

  const urls = findInDetails("urls");
  if (Array.isArray(urls) && urls.length > 0) {
    sessionStorage.setItem("urls", JSON.stringify(urls));
    sessionStorage.setItem("viewSiteMap", "true");
  }
}

/**
 * Top-level driver. Fetches the tracker for this user and hydrates
 * sessionStorage. Also captures the previously-selected setup method
 * (fast_setup / guided_setup) so the Continue Setup button routes
 * correctly without the caller having to re-derive it.
 *
 * Fire-and-forget safe — returns { method, hydrated } so callers can
 * decide whether to surface a "we restored your progress" toast or
 * just route silently. All errors are caught + logged; on failure we
 * return { method: null, hydrated: false } and the caller falls back
 * to the unhydrated route.
 *
 * @param {{ email?: string, userId?: string }} args
 * @returns {Promise<{ method: string|null, hydrated: boolean }>}
 */
export async function loadAndHydrateOnboardingTracker({ email, userId } = {}) {
  if (!email && !userId) {
    return { method: null, hydrated: false };
  }
  try {
    const resp = await getOnBoardingTracker({ email, userId });
    const tracker = resp?.tracker || resp?.data?.tracker || null;
    if (!tracker) return { method: null, hydrated: false };

    const details = Array.isArray(tracker?.details) ? tracker.details : [];

    // Capture the setup method (fast vs guided) and persist it the same
    // way Dashboard does, so handleCompleteSetup-style routers read
    // the right value.
    let method = null;
    const setupMethodStep = details.find(
      (d) => d.stepName === "setup_method_selected",
    );
    if (setupMethodStep?.data?.agent_creation_method) {
      method = setupMethodStep.data.agent_creation_method;
      sessionStorage.setItem("agent_creation_method1", method);
    }

    const stepsWithNo = details
      .filter((d) => typeof d.stepNo === "number")
      .sort((a, b) => a.stepNo - b.stepNo);

    const lastStep =
      stepsWithNo.length > 0 ? stepsWithNo[stepsWithNo.length - 1] : null;

    const payload = {
      email: tracker?.email,
      userId: tracker?.userId,
      steps: stepsWithNo,
      lastStep,
      rawDetails: details,
    };
    sessionStorage.setItem("onboarding-tracker", JSON.stringify(payload));

    hydrateSessionFromTracker(stepsWithNo, details, tracker);
    return { method, hydrated: true };
  } catch (err) {
    console.warn(
      "[onboardingHydrate] failed to load/hydrate tracker:",
      err?.message || err,
    );
    return { method: null, hydrated: false };
  }
}
